import React, {useCallback, useEffect, useState} from "react";
import {Box, Button, IconButton, Stack, Tooltip, Typography, styled} from "@mui/material";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import EmailIcon from '@mui/icons-material/Email';
import {checkStringProperty} from "../../../utility/componentUtil";
import CCOAccordion from "../../fundamentals/CCOAccordion";
import CCOLinkText, {type} from "../../fundamentals/CCOLinkText";
import {usePageContext} from "../../../context/PageContext";
import {useSessionContext} from "../../../context/SessionContext";
import {PARAMETER_CHANNEL_ID} from "../../../pages/config/settings";
import useWindowSize from "../../../utility/useWindowSize";
import {useTranslation} from "react-i18next";


interface Props {
    CAEGlobalSettings: any,
    padding?: number | string,
}

const StyledButton = styled(Button)(({ theme }) => ({
    minWidth: 'unset',
    width: '30px',
    height: '30px',
    borderRadius: '5px',
    "&:hover": {
        backgroundColor: 'var(--sos-main)',
        "& .MuiSvgIcon-root": {
            color: 'var(--background-white)',
        }
    }
}));

const toolTipProps = {
    tooltip: {
        sx: {
            bgcolor: 'var(--background-white)',
            color: 'var(--sos-main)',
            border: '1px solid var(--sos-main)',
            paddingTop: '0',
            paddingBottom: '0',
        }
    }
}

function C050BankContactData(props: Props) {
    const [dynamicIban, setDynamicIban] = useState();
    const pageContext = usePageContext();
    const session = useSessionContext();
    const {lowerThanWindowLarge} = useWindowSize()
    const {t} = useTranslation();
    const phone = props.CAEGlobalSettings["sos-phone"];
    const mail = props.CAEGlobalSettings["sos-email"];
    const accountTitle = props.CAEGlobalSettings["sos-title"] || "Spendenkonto";
    const marketingSettings = pageContext.page?.marketingSettings;
    const accountSettings = pageContext.page?.accountSettings;
    const channelIdSettings = marketingSettings ? marketingSettings["channelIDs"] : undefined;
    const channelAccountMappingSettings = accountSettings ? accountSettings["channel-account-mapping"] : undefined;
    const showDynamicIban = accountSettings ? accountSettings["sos-dynamic-iban"] : undefined;

    const channelId = session.sessionState[PARAMETER_CHANNEL_ID];

    const getIbanById = useCallback((id: string) => {
        if (!channelIdSettings) return;
        const matchingKey = Object.keys(channelIdSettings).find(
            key => channelIdSettings[key] === id
        );
        if (matchingKey) {
            return channelAccountMappingSettings[matchingKey];
        }
        return null;
    }, [channelIdSettings, channelAccountMappingSettings]);

    useEffect(() => {
        const iban = (getIbanById(channelId) || props.CAEGlobalSettings["sos-iban"])?.replace('IBAN', '') || '';
        setDynamicIban(iban);
    }, [channelId, getIbanById, props.CAEGlobalSettings]);

    const channelIdIban = showDynamicIban ? getIbanById(channelId) : null;

    const copyIban = () => {
        navigator.clipboard.writeText(channelIdIban || props.CAEGlobalSettings["sos-iban"])
            .catch((err) => {
                console.log('Error on copying IBAN: ' + err)
            });
    }
    const copyBank = () => {
        navigator.clipboard.writeText(props.CAEGlobalSettings["sos-bank"])
            .catch((err) => {
                console.log('Error copying bank information: ' + err)
            });
    }

    const box = <Box sx={{
        display: 'grid',
        alignSelf: 'start',
        padding: props.padding,
    }}>
        <Typography variant="h4" marginBottom={2}>Kontakt</Typography>
        <Stack direction="row" alignItems="center" gap={1} sx={{py: 0}}>
            <PhoneIphoneIcon fontSize={"small"} color={'primary'}/>
            {
                phone ? <a data-cypress='phoneContact' data-cypress-additional={`tel:${phone}`} href={`tel:${phone}`}>
                    <CCOLinkText type={type.FOOTER} txt={phone}/>
                </a> : null
            }
        </Stack>
        <Stack direction="row" alignItems="center" gap={1}>
            <EmailIcon fontSize={"small"} color={'primary'}/>
            {
                mail ? <a data-cypress='mailContact' data-cypress-additional={`mailto:${mail}`} href={`mailto:${mail}`}>
                    <CCOLinkText type={type.FOOTER} txt={mail}/>
                </a> : null
            }
        </Stack>

        <Typography variant="h4" marginTop={8}>{accountTitle}</Typography>

        <Stack direction="row" alignItems="center" gap={1} sx={{py: 2}}>
            <Typography data-cypress='bankName' variant="body1" fontSize={16}
                        p={0}>{props.CAEGlobalSettings["sos-bank"]}</Typography>
            <Tooltip title={t('c050_bank_name_copy_tooltip')} placement="top"
                     componentsProps={toolTipProps}>
                <StyledButton data-cypress='bankNameTooltipButton' aria-label="kopieren" onClick={copyBank}>
                    <ContentCopyIcon fontSize={"small"} color={'primary'}/>
                </StyledButton>
            </Tooltip>
        </Stack>
        <Stack direction="row" alignItems="center" gap={1} sx={{mb: 4}}>
            <Typography data-cypress='iban' variant="body1"
                        fontSize={16}><><b>IBAN </b>{dynamicIban}</>
            </Typography>
            <Tooltip title={t("c050_iban_copy_tooltip")} placement="top"
                     componentsProps={toolTipProps}>
                <StyledButton data-cypress='ibanTooltipButton' aria-label="kopieren" onClick={copyIban}>
                    <ContentCopyIcon fontSize={"small"} color={'primary'}/>
                </StyledButton>
            </Tooltip>
        </Stack>
        <div data-cypress='paypalButton'
            dangerouslySetInnerHTML={{__html: checkStringProperty(props.CAEGlobalSettings["sos-paypal-custom-button-markup"])}}/>
    </Box>

    return (<>
            {lowerThanWindowLarge ? <CCOAccordion title={"Kontakt"} initiallyExpanded
                                                  sx={{backgroundColor: 'transparent', borderTop: 'none'}}>
                    {box}
                </CCOAccordion>
                : box
            }
        </>
    )
}

export default C050BankContactData;